/**
 * `FontFamilyVariantDropdown` — composition-template Font control.
 *
 * Renders a compact closed-state button ("Family · Variant") that opens
 * an inline-expand picker mirroring `TextFontPicker`'s Font tab (the
 * gold-standard pattern captured under PLAT-9972):
 *
 *   - Family rows: chevron + family name + `AaBbCc` preview in the
 *     family's primary weight.
 *   - Click a family row → variants expand inline below; each variant's
 *     label is rendered in its own font-weight (so "Bold" looks bold).
 *   - Active variant: tinted-accent row background + accent text colour
 *     + `✓` checkmark on the right.
 *   - The family containing the currently-applied variant is auto-
 *     expanded on open so users see what's active without a click.
 *
 * The single font picker in the app — used on composition templates
 * (`FontControl.tsx`), Captions (`KaraokeSettings/TextSettingsUI.tsx`),
 * and any future surface that needs a font/variant picker. Graphics →
 * Text uses its own inline render (`TextFontPicker.tsx`'s Font tab,
 * which is the gold-standard pattern this dropdown mirrors). The legacy
 * `FontDropdownSelect` has been deleted.
 *
 * Some styled components are intentionally duplicated from
 * `src/components/sidepanel/TextFontPicker.tsx` rather than extracted
 * into a shared helper. The shared-helper refactor is the right long-
 * term move but is explicitly out of scope here (the user opted to
 * leave the Graphics → Text picker untouched).
 */

import { useBrandFontFamilies } from '@src/hooks/useBrandFontFamilies';
import {
  CAPTION_SCRIPT_LABELS,
  useCaptionScriptFonts,
} from '@src/hooks/useCaptionScriptFonts';
import { useFeatureFlags } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  ALL_CAPTION_FONT_FAMILIES,
  fontCoversScript,
} from '@src/utility/captionFonts';
import {
  getPopularFontWeights,
  isPopularFontVariantSelected,
} from '@src/utility/fonts';
import { familyLacksScript } from '@src/utility/fontScriptCoverage';
import { FONTS, JAPANESE_FONTS } from '@src/utility/general';
import { observer } from 'mobx-react-lite';
import {
  type CSSProperties,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import ArrowDown from '../../svgs/ArrowDown';
import CheckIcon from '../../svgs/CheckIcon';
import { useAnchoredDropdown } from './useAnchoredDropdown';

import type { FontFamily } from '@src/types.ts/story';

type Props = {
  /**
   * Called when a font is picked. For brand variants the third arg carries
   * the chosen variant's weight + style so the consumer can apply the exact
   * roman/italic face (popular fonts pass it as undefined).
   */
  action: (
    propertyName: string,
    value: string | null,
    variant?: { weight: number; style?: 'normal' | 'italic' },
  ) => void;
  propertyName: string;
  /**
   * PostScriptName (or font name for popular fonts) currently applied
   * to the element. Drives the closed-state label, the auto-expanded
   * family, and the active-variant highlight.
   */
  defaultValue: string | undefined;
  /**
   * Currently-applied font weight (as a string, e.g. `'700'`). Used only to
   * highlight the active row for *popular* fonts, which have no per-variant
   * postScriptName to match on. Omit for surfaces that don't track weight
   * separately — popular-font rows then simply render unselected.
   */
  fontWeight?: string;
  className?: string;
  /**
   * Always offer the Japanese fonts (`JAPANESE_FONTS`) regardless of the
   * active transcript/caption language, with no script sections or coverage
   * flags. Set for free-form composition text (overlays / nametags / cards),
   * where the author may type Japanese on any project (PLAT-10420). The
   * captions surface omits this and instead follows the active caption
   * script (PLAT-11415).
   */
  alwaysShowJapaneseFonts?: boolean;
  /**
   * Fixed menu width (px) for a compact trigger whose own width would make the
   * list too narrow — the captions bar (PLAT-12100). Omit to match the trigger.
   */
  menuWidth?: number;
};

const FontFamilyVariantDropdown = observer(
  forwardRef((props: Props, _ref) => {
    const videoCreator = useVideoCreatorStore();
    const branding = videoCreator.organization?.branding;

    // Brand families (structured model first; legacy `customFonts[]` fallback),
    // archived entries stripped. Shared resolver — see useBrandFontFamilies.
    const fontFamilies = useBrandFontFamilies();

    // Optimistic mirror of the applied value: picking a variant updates the
    // closed label + active highlight instantly, before `props.defaultValue`
    // round-trips back from the renderer. Re-synced whenever the prop changes
    // (a different element is selected, or the round-trip confirms the value).
    const [selectedValue, setSelectedValue] = useState(props.defaultValue);
    useEffect(() => {
      setSelectedValue(props.defaultValue);
    }, [props.defaultValue]);

    // Resolve the currently-applied variant + its family from the
    // postScriptName so the closed state can render "Family · Variant".
    const { activeFamily, activeVariant } = useMemo(() => {
      if (!selectedValue) {
        return { activeFamily: null, activeVariant: null };
      }
      const family = fontFamilies.find(f =>
        f.variants.some(v => v.postScriptName === selectedValue),
      );
      const variant =
        family?.variants.find(v => v.postScriptName === selectedValue) ?? null;
      return { activeFamily: family ?? null, activeVariant: variant };
    }, [selectedValue, fontFamilies]);

    // The display name we show in the closed-state button. Falls back
    // sensibly through legacy `customFonts[]`, then to the raw value.
    const closedDisplayLabel = useMemo(() => {
      if (activeFamily && activeVariant) {
        return `${activeFamily.name} · ${activeVariant.label}`;
      }
      const legacyMatch = branding?.customFonts?.find(
        f => f.postScriptName === selectedValue,
      );
      if (legacyMatch?.fontFamily) return legacyMatch.fontFamily;
      return selectedValue ?? 'Select font…';
    }, [activeFamily, activeVariant, branding?.customFonts, selectedValue]);

    // When the "Hide Arbor Fonts" flag is on, only brand/custom fonts are
    // offered — the curated popular (Arbor) font list is suppressed entirely.
    const { creatorStudioFontsHideArborFonts } = useFeatureFlags();

    // PLAT-11415 — script-aware captions picker, generalizing PLAT-10420's
    // Japanese-only gate. Free-form composition text keeps the always-offered
    // Japanese set (`alwaysShowJapaneseFonts`: authors may type Japanese on
    // any project). The captions surface (prop omitted) follows the active
    // caption script instead: the script's curated fonts get their own
    // leading section (pulled OUT of Popular Fonts, so overlapping families
    // like the Cyrillic set never render twice), and popular fonts that can't
    // render the script are flagged.
    const { script, scriptFonts } = useCaptionScriptFonts();
    const captionScript = props.alwaysShowJapaneseFonts ? 'latin' : script;
    // Re-derive from the effective script (not the hook's) so the label can
    // never name a script this surface has decided to ignore.
    const scriptLabel = CAPTION_SCRIPT_LABELS[captionScript];

    const customFamilyNames = useMemo(
      () => new Set(fontFamilies.map(f => f.name)),
      [fontFamilies],
    );
    const scriptSectionFonts = useMemo(() => {
      if (creatorStudioFontsHideArborFonts) return [];
      if (captionScript === 'latin') return [];
      return scriptFonts.filter(name => !customFamilyNames.has(name));
    }, [
      captionScript,
      scriptFonts,
      customFamilyNames,
      creatorStudioFontsHideArborFonts,
    ]);
    const popularFonts = useMemo(() => {
      if (creatorStudioFontsHideArborFonts) return [];
      const names = props.alwaysShowJapaneseFonts
        ? [...FONTS, ...JAPANESE_FONTS]
        : FONTS;
      const scriptSet = new Set(scriptSectionFonts);
      return names.filter(
        name => !customFamilyNames.has(name) && !scriptSet.has(name),
      );
    }, [
      customFamilyNames,
      creatorStudioFontsHideArborFonts,
      props.alwaysShowJapaneseFonts,
      scriptSectionFonts,
    ]);

    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // `currentFontName`: the family/font name that holds the active
    // variant — used to auto-expand on open and mark the family row.
    const currentFontName = useMemo(() => {
      if (activeFamily) return activeFamily.name;
      if (
        selectedValue &&
        (FONTS.includes(selectedValue as (typeof FONTS)[number]) ||
          // PLAT-11415 — any curated caption family (Japanese, Cyrillic,
          // Chinese, Korean sets + substitutes) counts as a known popular font.
          ALL_CAPTION_FONT_FAMILIES.includes(selectedValue))
      ) {
        return selectedValue;
      }
      return null;
    }, [activeFamily, selectedValue]);

    const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(() =>
      currentFontName ? new Set([currentFontName]) : new Set(),
    );

    // Re-seed expandedFamilies when the dropdown reopens — ensures the
    // currently-applied family is always visible without a click.
    useEffect(() => {
      if (isOpen && currentFontName) {
        setExpandedFamilies(prev => {
          if (prev.has(currentFontName)) return prev;
          const next = new Set(prev);
          next.add(currentFontName);
          return next;
        });
      }
    }, [isOpen, currentFontName]);

    const toggleFamily = (name: string) => {
      setExpandedFamilies(prev => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        return next;
      });
    };

    const closeDropdown = () => {
      setIsOpen(false);
      setSearchQuery('');
    };

    // Portal the menu to <body>, anchored directly below the trigger and
    // overlaying everything (no panel-overflow clipping). The hook owns
    // dismissal — click-outside (trigger + menu), scroll, resize, Escape —
    // and resets search via closeDropdown.
    const triggerRef = useRef<HTMLButtonElement>(null);
    const { menuRef, style: menuStyle } = useAnchoredDropdown(
      isOpen,
      triggerRef,
      closeDropdown,
      undefined,
      props.menuWidth ? { width: props.menuWidth } : undefined,
    );

    const handlePick = (
      postScriptNameOrFontName: string,
      variant?: { weight: number; style?: 'normal' | 'italic' },
    ) => {
      // Reflect the pick immediately; `props.defaultValue` will catch up once
      // the change round-trips through the renderer.
      setSelectedValue(postScriptNameOrFontName);
      props.action(props.propertyName, postScriptNameOrFontName, variant);
      closeDropdown();
    };

    // Search filters by case-insensitive substring on the family name.
    // Empty query keeps everything visible.
    const q = searchQuery.trim().toLowerCase();
    const filteredBrandFamilies = useMemo(
      () =>
        q
          ? fontFamilies.filter(f => f.name.toLowerCase().includes(q))
          : fontFamilies,
      [fontFamilies, q],
    );
    const filteredPopularFonts = useMemo(
      () =>
        q
          ? popularFonts.filter(n => n.toLowerCase().includes(q))
          : popularFonts,
      [popularFonts, q],
    );
    const filteredScriptFonts = useMemo(
      () =>
        q
          ? scriptSectionFonts.filter(n => n.toLowerCase().includes(q))
          : scriptSectionFonts,
      [scriptSectionFonts, q],
    );

    const renderFamilyRow = (name: string, family: FontFamily | undefined) => {
      const isExpanded = expandedFamilies.has(name);
      const isCurrent = family
        ? family.variants.some(v => v.postScriptName === selectedValue)
        : name === currentFontName;
      // PLAT-11415 — flag fonts that can't render the active caption script
      // ("clearly warned" rather than blocked — the canvas shows the
      // consequence immediately, and DE1 respects any covering re-pick).
      // Popular fonts check the curated coverage map; brand families check
      // the cmap coverage recorded at upload (PLAT-11412) — families
      // uploaded before that have no coverage data and stay unflagged.
      const showsNoGlyphs =
        captionScript !== 'latin' &&
        (family
          ? familyLacksScript(
              family.variants.filter(v => !v.archivedAt),
              captionScript,
            )
          : !fontCoversScript(name, captionScript));

      // Preview the family header (name + AaBbCc) in a representative face:
      // brand faces are loaded by useLoadBrandKitFonts keyed by postScriptName;
      // popular fonts resolve by family name (all loaded via globals.css).
      // Quote the token so multi-word names stay valid CSS.
      const repVariant = family
        ? (family.variants.find(v => !v.archivedAt && v.style === 'normal') ??
          family.variants.find(v => !v.archivedAt))
        : undefined;
      const headerFont: CSSProperties = repVariant
        ? {
            fontFamily: `"${repVariant.postScriptName}", Inter`,
            fontWeight: repVariant.weight,
            fontStyle: repVariant.style,
          }
        : { fontFamily: `"${name}", Inter` };

      return (
        <SFontEntry key={name}>
          <SFontRow isCurrent={isCurrent} onClick={() => toggleFamily(name)}>
            <SChevron isExpanded={isExpanded}>
              <ArrowDown
                width="10"
                height="10"
                strokeColor={isCurrent ? 'var(--accent)' : 'var(--text-muted)'}
              />
            </SChevron>
            <SFontNameLabel style={headerFont}>{name}</SFontNameLabel>
            <SPreview style={headerFont}>AaBbCc</SPreview>
            {showsNoGlyphs && (
              <SCoverageHint>{`No ${scriptLabel}`}</SCoverageHint>
            )}
          </SFontRow>

          {isExpanded && (
            <SVariantList>
              {family
                ? family.variants
                    .filter(v => !v.archivedAt)
                    .map(v => {
                      const isSelected = v.postScriptName === selectedValue;
                      return (
                        <SVariantRow
                          key={v.artifactId}
                          isSelected={isSelected}
                          onClick={() =>
                            handlePick(v.postScriptName, {
                              weight: v.weight,
                              style: v.style,
                            })
                          }
                        >
                          <SVariantLabel
                            isSelected={isSelected}
                            style={{
                              fontFamily: `"${v.postScriptName}", Inter`,
                              fontWeight: v.weight,
                              fontStyle: v.style,
                            }}
                          >
                            {v.label}
                          </SVariantLabel>
                          {isSelected && (
                            <CheckIcon
                              width="10"
                              height="10"
                              fillColor="var(--accent)"
                            />
                          )}
                        </SVariantRow>
                      );
                    })
                : getPopularFontWeights(name).map(bv => {
                    // Popular fonts don't carry per-variant postScriptNames —
                    // Creatomate resolves them from the family name + weight on
                    // the element. Highlight the row whose weight matches the
                    // element's applied `font_weight` (passed via the
                    // `fontWeight` prop). When the consumer doesn't track weight
                    // (prop omitted) no row is marked — matches TextFontPicker.
                    const isSelected = isPopularFontVariantSelected(
                      name,
                      currentFontName,
                      bv.fontWeight,
                      props.fontWeight,
                    );
                    return (
                      <SVariantRow
                        key={bv.fontSubfamily}
                        isSelected={isSelected}
                        // Pass the row's weight so picking "Semi-Bold"
                        // actually applies it (brand rows already do). No
                        // style: popular italics are the separate toggle, and
                        // a weight pick must not reset it.
                        onClick={() =>
                          handlePick(name, { weight: bv.fontWeight })
                        }
                      >
                        <SVariantLabel
                          isSelected={isSelected}
                          style={{
                            // Popular fonts are loaded via the Google Fonts
                            // import in globals.css; render each weight in its
                            // own face. Quote for multi-word family names.
                            fontFamily: `"${name}", Inter`,
                            fontWeight: bv.fontWeight,
                          }}
                        >
                          {bv.fontSubfamily}
                        </SVariantLabel>
                        {isSelected && (
                          <CheckIcon
                            width="10"
                            height="10"
                            fillColor="var(--accent)"
                          />
                        )}
                      </SVariantRow>
                    );
                  })}
            </SVariantList>
          )}
        </SFontEntry>
      );
    };

    return (
      <SContainer className={props.className}>
        <SClosedButton
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen(o => !o)}
          aria-expanded={isOpen}
        >
          <SClosedLabel
            style={
              activeVariant
                ? {
                    fontFamily: `"${activeVariant.postScriptName}", Inter`,
                    fontWeight: activeVariant.weight,
                    fontStyle: activeVariant.style,
                  }
                : {
                    fontFamily: `"${activeFamily?.name ?? selectedValue ?? 'Inter'}", Inter`,
                  }
            }
          >
            {closedDisplayLabel}
          </SClosedLabel>
          <SClosedChevron isOpen={isOpen}>
            <ArrowDown width="10" height="10" strokeColor="var(--accent)" />
          </SClosedChevron>
        </SClosedButton>

        {isOpen &&
          menuStyle &&
          createPortal(
            <SDropdown
              ref={menuRef}
              style={menuStyle}
              // Picking a font is not a click away from the canvas selection
              // (PLAT-12102 DS6; see floatingToolbar/ToolbarLayer).
              data-keeps-canvas-selection=""
            >
              <SSearchInput
                autoFocus
                placeholder="Search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <SFontList>
                {/* PLAT-11415 — the active caption script's curated fonts,
                    surfaced first so translated captions find a working font
                    without scrolling. Empty for Latin. */}
                {filteredScriptFonts.length > 0 && (
                  <>
                    <SSectionLabel>{`Fonts for ${scriptLabel}`}</SSectionLabel>
                    {filteredScriptFonts.map(name =>
                      renderFamilyRow(name, undefined),
                    )}
                  </>
                )}
                {filteredBrandFamilies.length > 0 && (
                  <>
                    <SSectionLabel>Brand Fonts</SSectionLabel>
                    {filteredBrandFamilies.map(f => renderFamilyRow(f.name, f))}
                  </>
                )}
                {filteredPopularFonts.length > 0 && (
                  <>
                    <SSectionLabel>Popular Fonts</SSectionLabel>
                    {filteredPopularFonts.map(name =>
                      renderFamilyRow(name, undefined),
                    )}
                  </>
                )}
                {filteredScriptFonts.length === 0 &&
                  filteredBrandFamilies.length === 0 &&
                  filteredPopularFonts.length === 0 && (
                    <SEmptyResults>
                      No fonts match "{searchQuery}"
                    </SEmptyResults>
                  )}
              </SFontList>
            </SDropdown>,
            document.body,
          )}
      </SContainer>
    );
  }),
);

export default FontFamilyVariantDropdown;

// --- styled components -----------------------------------------------------

const SContainer = styled.div`
  position: relative;
  width: 100%;
  font-family: 'Inter', sans-serif;
`;

const SClosedButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: transparent;
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: var(--text-muted);
  }
  &:focus-visible {
    /*
     * Border-only focus per the toned-down standard in
     * docs/design-system.md "Form Fields (Bordered Primitives)".
     */
    border-color: var(--accent);
  }
`;

const SClosedLabel = styled.span`
  flex: 1;
  text-align: left;
  /* The closed-state label's font (family + weight + style) is applied
   * inline from the active variant so users see not just the *name* of the
   * font but its visual identity. Falls back to Inter when none resolves. */
  font-size: 14px;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SClosedChevron = styled.span<{ isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 150ms ease;
`;

// Positioning (position/top/left/width/max-height/overflow/z-index) is
// supplied at runtime by useAnchoredDropdown via an inline style — the menu
// portals to <body> and anchors below the trigger. Only visuals live here.
const SDropdown = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
  box-shadow: 8px 16px 8px 0 rgba(0, 0, 0, 0.4);
`;

const SSearchInput = styled.input`
  /*
   * 'box-sizing: border-box' is required because the input has its own
   * padding + 1px border. Without it, 'width: 100%' resolves to the
   * dropdown's content width *plus* the input's own padding + border,
   * which pushes the right edge past the dropdown's rounded corner.
   */
  box-sizing: border-box;
  width: 100%;
  height: 34px;
  /* Never let the flex column squash the search field — the list below
   * absorbs any height clamp instead (see SFontList flex: 1 / min-height: 0). */
  min-height: 34px;
  flex-shrink: 0;
  padding: 0 10px;
  margin-bottom: 6px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  outline: none;
  transition: border-color var(--transition-fast);
  &:hover {
    border-color: var(--text-muted);
  }
  &:focus {
    border-color: var(--accent);
  }
  &::placeholder {
    color: var(--text-muted);
  }
`;

const SFontList = styled.div`
  display: flex;
  flex-direction: column;
  /* Absorb any height clamp from the portaled menu so the search field
   * above keeps its full height; the list scrolls instead. */
  flex: 1;
  min-height: 0;
  max-height: 280px;
  overflow-y: auto;
  scrollbar-color: var(--gray-400) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
`;

const SSectionLabel = styled.span`
  display: block;
  padding: 8px 4px 2px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  letter-spacing: 0.4px;
`;

const SFontEntry = styled.div`
  display: flex;
  flex-direction: column;
`;

const SFontRow = styled.div<{ isCurrent?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 4px;
  border-radius: 6px;
  cursor: pointer;
  ${({ isCurrent }) =>
    isCurrent &&
    css`
      background: color-mix(in srgb, var(--accent) 12%, transparent);
    `}
  &:hover {
    background: ${({ isCurrent }) =>
      isCurrent
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

const SChevron = styled.div<{ isExpanded: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: ${({ isExpanded }) =>
    isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'};
  transition: transform 150ms ease;
`;

const SFontNameLabel = styled.span`
  flex: 0 1 auto;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const SPreview = styled.span`
  flex-shrink: 0;
  /* font-family / weight applied inline (representative face of the family). */
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

/* PLAT-11415 — "No Japanese" caveat on popular fonts that can't render the
 * active caption script. Muted + italic, NOT accent: inside this dropdown
 * gold already means "current/selected" (row tint, chevron, check), and the
 * theme has no standalone inline-warning token. Trails the AaBbCc preview so
 * the preview keeps its position on unflagged rows. */
const SCoverageHint = styled.span`
  flex-shrink: 0;
  font-size: var(--text-xs);
  font-style: italic;
  color: var(--text-muted);
  white-space: nowrap;
`;

const SVariantList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2px 0 6px;
`;

const SVariantRow = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding: 0 8px 0 28px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ isSelected }) =>
    isSelected
      ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
      : 'transparent'};
  &:hover {
    background: ${({ isSelected }) =>
      isSelected
        ? 'color-mix(in srgb, var(--accent) 18%, transparent)'
        : 'var(--surface-elevated)'};
  }
`;

const SVariantLabel = styled.span<{ isSelected: boolean }>`
  font-size: var(--text-sm);
  /* font-family / weight / style applied inline per variant. */
  color: ${({ isSelected }) =>
    isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
`;

const SEmptyResults = styled.div`
  padding: 12px 8px;
  color: var(--text-muted);
  font-size: var(--text-sm);
  text-align: center;
`;
